import React, { useState, useEffect } from 'react';
import { createLead, updateLead, getUsers } from '../api/leadsApi';
import { useAuth } from '../context/AuthContext';

const SOURCES = [
  { value: 'upwork', label: 'Upwork' },
  { value: 'client_referral', label: 'Client Referral' },
  { value: 'personal_referral', label: 'Personal / Friend Referral' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'inbound', label: 'Inbound (they reached out)' },
  { value: 'other', label: 'Other' },
];

const AI_USE_CASES = [
  { value: 'workflow_automation', label: 'Workflow Automation' },
  { value: 'autonomous_agents', label: 'Autonomous AI Agents' },
  { value: 'chatbot', label: 'Chatbot / Conversational AI' },
  { value: 'data_processing', label: 'Data Processing & Analysis' },
  { value: 'customer_service', label: 'Customer Service Automation' },
  { value: 'sales_automation', label: 'Sales Automation' },
  { value: 'custom', label: 'Custom (specify in notes)' },
];

const DEAL_SIZES = [
  { value: 'small', label: 'Small (< $5k)' },
  { value: 'medium', label: 'Medium ($5k–$20k)' },
  { value: 'large', label: 'Large ($20k–$50k)' },
  { value: 'enterprise', label: 'Enterprise ($50k+)' },
];

const TIMELINES = [
  { value: 'immediate', label: 'Immediate (< 2 weeks)' },
  { value: 'short', label: 'Short (2–4 weeks)' },
  { value: 'medium', label: 'Medium (1–3 months)' },
  { value: 'long', label: 'Long (3+ months)' },
  { value: 'unknown', label: 'Unknown' },
];

export default function LeadForm({ onClose, onSaved, initialLead }) {
  const isEdit = !!initialLead;
  const { isSuperAdmin } = useAuth();
  const [users, setUsers] = useState([]);

  useEffect(() => {
    if (isSuperAdmin()) {
      getUsers().then(r => setUsers(r.data.filter(u => u.isActive))).catch(() => {});
    }
  }, []);

  const [form, setForm] = useState({
    name: initialLead?.name || '',
    company: initialLead?.company || '',
    email: initialLead?.email || '',
    phone: initialLead?.phone || '',
    linkedinUrl: initialLead?.linkedinUrl || '',
    source: initialLead?.source || 'upwork',
    aiUseCase: initialLead?.aiUseCase || '',
    dealSize: initialLead?.dealSize || '',
    budgetEstimate: initialLead?.budgetEstimate || '',
    timeline: initialLead?.timeline || 'unknown',
    isUpworkClient: initialLead?.isUpworkClient || false,
    upworkJobUrl: initialLead?.upworkJobUrl || '',
    notes: initialLead?.notes || '',
    assignedTo: initialLead?.assignedTo || '',
    assignedToName: initialLead?.assignedToName || '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setAssignee = (userId) => {
    const u = users.find(u => u._id === userId);
    setForm(f => ({ ...f, assignedTo: userId, assignedToName: u ? u.name : '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.company || !form.source) { setError('Name, company and source are required.'); return; }
    setLoading(true); setError('');
    try {
      if (isEdit) {
        await updateLead(initialLead._id, form);
      } else {
        await createLead(form);
      }
      onSaved();
    } catch (err) {
      setError(err.response?.data?.message || `Failed to ${isEdit ? 'update' : 'create'} lead.`);
    } finally { setLoading(false); }
  };

  return (
    <div style={s.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={s.modal}>
        <div style={s.header}>
          <h2 style={s.title}>{isEdit ? `Edit — ${initialLead.name}` : 'Add new lead'}</h2>
          <button style={s.close} onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit} style={s.form}>
          <Section label="Contact details">
            <Row>
              <Field label="Full name *"><input style={s.input} value={form.name} onChange={e => set('name', e.target.value)} placeholder="John Smith" /></Field>
              <Field label="Company *"><input style={s.input} value={form.company} onChange={e => set('company', e.target.value)} placeholder="Acme Corp" /></Field>
            </Row>
            <Row>
              <Field label="Email"><input style={s.input} type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="john@acme.com" /></Field>
              <Field label="Phone"><input style={s.input} value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="+1 555 0100" /></Field>
            </Row>
            <Field label="LinkedIn URL"><input style={s.input} value={form.linkedinUrl} onChange={e => set('linkedinUrl', e.target.value)} placeholder="https://linkedin.com/in/..." /></Field>
          </Section>

          <Section label="Lead classification">
            <Row>
              <Field label="Lead source *">
                <select style={s.input} value={form.source} onChange={e => set('source', e.target.value)}>
                  {SOURCES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </Field>
              <Field label="AI use case">
                <select style={s.input} value={form.aiUseCase} onChange={e => set('aiUseCase', e.target.value)}>
                  <option value="">Select use case</option>
                  {AI_USE_CASES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </Field>
            </Row>
            <Row>
              <Field label="Deal size estimate">
                <select style={s.input} value={form.dealSize} onChange={e => set('dealSize', e.target.value)}>
                  <option value="">Select size</option>
                  {DEAL_SIZES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </Field>
              <Field label="Budget (USD)">
                <input style={s.input} type="number" value={form.budgetEstimate} onChange={e => set('budgetEstimate', e.target.value)} placeholder="e.g. 10000" />
              </Field>
            </Row>
            <Field label="Timeline to start">
              <select style={s.input} value={form.timeline} onChange={e => set('timeline', e.target.value)}>
                {TIMELINES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </Field>
          </Section>

          {form.source === 'upwork' && (
            <Section label="Upwork details">
              <Field label="Upwork job URL"><input style={s.input} value={form.upworkJobUrl} onChange={e => set('upworkJobUrl', e.target.value)} placeholder="https://upwork.com/jobs/..." /></Field>
              <label style={s.checkLabel}>
                <input type="checkbox" checked={form.isUpworkClient} onChange={e => set('isUpworkClient', e.target.checked)} />
                &nbsp; This is an existing Upwork client (retainer conversion candidate)
              </label>
            </Section>
          )}

          {isSuperAdmin() && users.length > 0 && (
            <Section label="Assignment">
              <Field label="Assign to">
                <select style={s.input} value={form.assignedTo} onChange={e => setAssignee(e.target.value)}>
                  <option value="">Unassigned</option>
                  {users.map(u => <option key={u._id} value={u._id}>{u.name} {u.role === 'super_admin' ? '(Admin)' : ''}</option>)}
                </select>
              </Field>
            </Section>
          )}

          <Section label="Notes">
            <textarea style={{ ...s.input, minHeight: 80, resize: 'vertical' }} value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Key details about this lead, their pain points, what they're looking to build..." />
          </Section>

          {error && <div style={s.error}>{error}</div>}
          <div style={s.actions}>
            <button type="button" style={s.cancelBtn} onClick={onClose}>Cancel</button>
            <button type="submit" style={s.submitBtn} disabled={loading}>
              {loading ? (isEdit ? 'Saving...' : 'Adding...') : (isEdit ? 'Save changes' : 'Add lead to pipeline')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Section({ label, children }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10, paddingBottom: 6, borderBottom: '1px solid #f0efe9' }}>{label}</div>
      {children}
    </div>
  );
}
function Row({ children }) { return <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>{children}</div>; }
function Field({ label, children }) { return <div><label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4, fontWeight: 500 }}>{label}</label>{children}</div>; }

const s = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: 16 },
  modal: { background: '#fff', borderRadius: 16, width: '100%', maxWidth: 600, maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #f0efe9' },
  title: { fontSize: 16, fontWeight: 600, margin: 0, color: '#1a1a1a' },
  close: { background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#888', lineHeight: 1 },
  form: { padding: '20px', overflowY: 'auto', flex: 1 },
  input: { width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #e0e0e0', fontSize: 13, boxSizing: 'border-box', outline: 'none', color: '#1a1a1a', background: '#fafafa' },
  checkLabel: { fontSize: 13, color: '#555', display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' },
  error: { background: '#fff0f0', color: '#c0392b', padding: '10px 12px', borderRadius: 8, fontSize: 13, marginBottom: 12 },
  actions: { display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 },
  cancelBtn: { padding: '9px 18px', borderRadius: 8, border: '1px solid #e0e0e0', background: '#fff', fontSize: 13, cursor: 'pointer', color: '#555' },
  submitBtn: { padding: '9px 20px', borderRadius: 8, border: 'none', background: '#534AB7', color: '#fff', fontSize: 13, fontWeight: 500, cursor: 'pointer' },
};
